import { useMemo } from 'react';
import type { IReplayEvent } from '@interfaces/IMonitorApi';
import type { IWorkingHours } from '@interfaces/IWorkingHours';
import { dayMarkers } from './markers';
import { MarkerViewFooter } from './MarkerViewFooter';
import { ReplayTrack } from './ReplayTrack';
import { shiftFor } from './replayDays';
import type { IShownTrack } from './shownTrack';
import { useMarkerView } from './useMarkerView';
import barStyles from './ReplayBar.module.css';
import trackStyles from './ReplayTrack.module.css';

const LOOK_CLASS: Record<IShownTrack['look'], string | undefined> = {
  loaded: undefined,
  held: barStyles.held,
  skeleton: `${barStyles.skeleton} ${trackStyles.skeleton}`,
};

interface IDayTrackProps {
  shown: IShownTrack;
  workingHours: IWorkingHours | undefined;
  onSeek(ms: number, commit: boolean): void;
  onMarker(event: IReplayEvent): void;
}

/**
 * The bar's day track and its legend, in one of three looks. The markers and the
 * shift lines are placed once per day, never per playback tick.
 */
export function DayTrack({ shown, workingHours, onSeek, onMarker }: IDayTrackProps) {
  const [markerView, toggleMarkerView] = useMarkerView();
  const { day, atMs, look } = shown;
  const markers = useMemo(() => dayMarkers(day), [day]);
  const shift = useMemo(() => shiftFor(day.date, day.timezone, workingHours), [day, workingHours]);
  return (
    <div className={LOOK_CLASS[look]} inert={look !== 'loaded'} data-track={look}>
      <ReplayTrack
        replay={day}
        atMs={atMs}
        tz={day.timezone}
        shift={shift}
        markers={markers}
        markerView={markerView}
        onSeek={onSeek}
        onMarker={onMarker}
      />
      <MarkerViewFooter markers={markers} view={markerView} onToggle={toggleMarkerView} />
    </div>
  );
}
